import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation, matchPath } from 'react-router-dom'
//contexts
import { useApplicationContext } from '../../../../context/ApplicationContext'
//components
import { Message } from '@paypalcorp/worldready-react'
import TopNavToggleMessage, {
  DEFAULT_DIALOG,
  WEBLLS_REMEMBERED_USER_DIALOG,
  UNAUTHORIZED_LIVE_ENV_ROUTE_APPS_DIALOG,
  UNAUTHORIZED_LIVE_ENV_ROUTE_SANDBOX_DIALOG,
  UNAUTHORIZED_LIVE_ENV_ROUTE_HOME_DIALOG,
  UNCONFIRMED_EMAIL_DIALOG,
  UNCONFIRMED_EMAIL_SECONDARY_USER_DIALOG,
  MINIMAL_ACCOUNT_DIALOG,
  MISSING_SB_ACCOUNT_DETAILS_DIALOG,
  LIVE_NOT_ALLOWED,
  STEPUP_NEEDED,
} from '../top-nav-toggle-message'
//utils
import _filter from 'lodash.filter'
import { getUserType } from '../../../../utils/user-utils'
import { clearLocalStorage } from '../../../../utils/session-utils'
//constants
import {
  topNavToggleItems,
  unauthorizedLiveEnvToggleUrls,
} from './top-nav-toggle-scheme'
import { recordDynamicEvent } from '../../analytics/utils/record-dynamic-event'
import { useComponentVisible } from '../../../../hooks'

export const TopNavToggle = React.memo(() => {
  const {
    enviroment,
    setCurrentEnvironment,
    setReloadPage,
    currentBannerStep,
    user,
    isWebLLSUser,
    isConfirmedEmail,
    processedMinAcct,
    secondaryUser,
    liveApiAllowed,
    stepUpNeeded,
    nvpSoapAppData,
    setCurrentAppValue,
  } = useApplicationContext()
  const navigate = useNavigate()
  const {
    ref,
    isComponentVisible,
    setIsComponentVisible,
  } = useComponentVisible(true)

  const { pathname, search } = useLocation()
  const [onClickSwitcher, setOnClickSwitcher] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [dialog, setDialog] = useState(false)
  const accountType = getUserType(user)
  const isLive = enviroment === 'live'
  let restrictedPathType
  unauthorizedLiveEnvToggleUrls.forEach((restrictedURL) => {
    if (matchPath(restrictedURL.url, pathname)) {
      restrictedPathType = restrictedURL.urlType
      if (
        restrictedURL.exceptionUrlContainsParameters &&
        search.includes(restrictedURL.exceptionUrlContainsParameters) &&
        nvpSoapAppData?.sbCredentials?.client_id
      ) {
        restrictedPathType = null
      }
    }
  })

  // eslint-disable-next-line complexity
  const needAlert = () => {
    const alert = {
      show: false,
      type: undefined,
    }
    if (enviroment === 'sandbox' && accountType !== 'BUSINESS') {
      alert.show = true
      alert.type = DEFAULT_DIALOG
    } else if (enviroment === 'sandbox' && liveApiAllowed === false) {
      alert.show = true
      if (stepUpNeeded) {
        alert.type = STEPUP_NEEDED
      } else {
        alert.type = LIVE_NOT_ALLOWED
      }
    } else if (!isConfirmedEmail) {
      alert.show = true
      if (secondaryUser) {
        alert.type = UNCONFIRMED_EMAIL_SECONDARY_USER_DIALOG
      } else {
        alert.type = UNCONFIRMED_EMAIL_DIALOG
      }
    } else if (enviroment === 'sandbox' && isWebLLSUser) {
      alert.show = true
      alert.type = WEBLLS_REMEMBERED_USER_DIALOG
    } else if (processedMinAcct?.isMinAccount) {
      alert.show = true
      alert.type = MINIMAL_ACCOUNT_DIALOG
    } else if (restrictedPathType) {
      //restrict ability to toggle environments for urls specific to applications
      //in the current environment
      const APP_CREDENTIALS = 'apps&credentials'
      const SANDBOX_ACCOUNT = 'sandboxAccounts'
      alert.show = true
      if (
        enviroment === 'live' &&
        restrictedPathType === APP_CREDENTIALS &&
        nvpSoapAppData?.openIdConnect === 'Y' &&
        !nvpSoapAppData?.sbCredentials?.client_id
      ) {
        alert.type = MISSING_SB_ACCOUNT_DETAILS_DIALOG
      } else if (restrictedPathType === APP_CREDENTIALS) {
        alert.type = UNAUTHORIZED_LIVE_ENV_ROUTE_APPS_DIALOG
      } else if (restrictedPathType === SANDBOX_ACCOUNT) {
        alert.type = UNAUTHORIZED_LIVE_ENV_ROUTE_SANDBOX_DIALOG
      } else {
        alert.type = UNAUTHORIZED_LIVE_ENV_ROUTE_HOME_DIALOG
      }
    }
    return alert
  }
  useEffect(() => {
    if (!isComponentVisible) {
      setOpenDialog(false)
    }
  }, [isComponentVisible])
  const onClickHandler = async () => {
    clearLocalStorage('appSelection')
    const alert = needAlert()
    if (alert.show === true) {
      recordDynamicEvent(
        `Environment-toggle,click,switch disabled - alert: ${alert.type}`,
      )
      setOnClickSwitcher(false)
      setOpenDialog(true)
      setIsComponentVisible(true)
      setDialog(alert.type)
    } else {
      const targetEnvironment = enviroment === 'sandbox' ? 'live' : 'sandbox'
      recordDynamicEvent(
        `Environment-toggle,click,environment toggle switch to ${targetEnvironment}`,
      )
      const filteredResult = _filter(topNavToggleItems, [
        enviroment,
        location.pathname,
      ])
      setCurrentEnvironment(targetEnvironment)
      setCurrentAppValue(undefined)
      const closeOnboardingBanner =
        currentBannerStep?.closeOnboardingBanner || false
      if (
        closeOnboardingBanner ||
        !(
          currentBannerStep?.bannerPage === 2 ||
          currentBannerStep?.bannerPage === 4
        )
      ) {
        setReloadPage('yes')
      }
      if (filteredResult.length > 0) {
        const targetUrl = filteredResult[0][targetEnvironment]
        // remove the /dashboard from url to pass to navigate
        const navigateTo = targetUrl.slice(targetUrl.indexOf('/', 2))

        /* eslint-disable no-unused-expressions */
        navigateTo.includes('transactions/sandbox')
          ? navigate('/')
          : navigate(navigateTo)
      }
      setOnClickSwitcher(true)
    }
  }

  const onCloseHandler = () => {
    setOpenDialog(false)
  }

  const handleLivePageForWebLLSUser = () => {
    setCurrentEnvironment('sandbox')
    setReloadPage('yes')
  }

  return (
    <>
      <div className="dev_dash__top-nav-switcher-flex">
        {isLive &&
          (isWebLLSUser || liveApiAllowed === false) &&
          handleLivePageForWebLLSUser()}
        {isLive && !onClickSwitcher && (
          <div
            className="dev_dash__top-nav-switcher-block"
            id="toggle_sandbox_live"
          >
            <div className="dev_dash__top-nav-switcher-label-gray-black">
              <Message id="top-nav.sandbox.label" />
            </div>
            <div
              className="dev_dash__top-nav-switcher-container"
              onClick={onClickHandler}
              data-cy="env-toggle-switch-sandbox"
            >
              <div className="dev_dash__top-nav-switcher-live-background">
                <div className="dev_dash__top-nav-switcher-live-circle" />
              </div>
            </div>
            <div className="dev_dash__top-nav-switcher-label-black-gray">
              <Message id="top-nav.live.label" />
            </div>
            {openDialog && (
              <TopNavToggleMessage
                dialogType={dialog}
                accountType={accountType}
                onCloseHandler={onCloseHandler}
              />
            )}
          </div>
        )}
        {enviroment === 'sandbox' && !onClickSwitcher && (
          <>
            <div
              className="dev_dash__top-nav-switcher-block"
              id="toggle_sandbox_live"
            >
              <div className="dev_dash__top-nav-switcher-label-black-gray">
                <Message id="top-nav.sandbox.label" />
              </div>
              <div
                className="dev_dash__top-nav-switcher-container"
                onClick={onClickHandler}
                data-cy="env-toggle-switch-sandbox"
              >
                <div className="dev_dash__top-nav-switcher-sandbox-background">
                  <div className="dev_dash__top-nav-switcher-sandbox-circle" />
                </div>
              </div>
              <div className="dev_dash__top-nav-switcher-label-gray-black">
                <Message id="top-nav.live.label" />
              </div>
              <div ref={ref}>
                {openDialog && (
                  <TopNavToggleMessage
                    dialogType={dialog}
                    accountType={accountType}
                    onCloseHandler={onCloseHandler}
                  />
                )}
              </div>
            </div>
          </>
        )}
        {onClickSwitcher && (
          <div
            className="dev_dash__top-nav-switcher-block"
            id="toggle_sandbox_live"
          >
            <div
              className={
                isLive
                  ? 'dev_dash__top-nav-switcher-label-black-gray-animation'
                  : 'dev_dash__top-nav-switcher-label-gray-black-animation'
              }
            >
              <Message id="top-nav.sandbox.label" />
            </div>
            <div
              className="dev_dash__top-nav-switcher-container"
              onClick={onClickHandler}
              data-cy="env-toggle-switch-live"
            >
              <div
                className={
                  isLive
                    ? 'dev_dash__top-nav-switcher-sandbox-live-background-animation'
                    : 'dev_dash__top-nav-switcher-live-sandbox-background-animation'
                }
              >
                <div
                  className={
                    isLive
                      ? 'dev_dash__top-nav-switcher-sandbox-live-circle-animation'
                      : 'dev_dash__top-nav-switcher-sandbox-circle-animation'
                  }
                />
              </div>
            </div>
            <div
              className={
                isLive
                  ? 'dev_dash__top-nav-switcher-label-gray-black-animation'
                  : 'dev_dash__top-nav-switcher-label-black-gray-animation'
              }
            >
              <Message id="top-nav.live.label" />
            </div>
          </div>
        )}
      </div>
    </>
  )
})

export default TopNavToggle
